import { Fragment, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronRight, LayoutList } from "lucide-react";
import { PageHeader, EmbeddedContext } from "@/components/crm/common";
import { useAuth } from "@/context/AuthContext";
import Assignments from "@/pages/Assignments";
import Tracker from "@/pages/Tracker";
import Handover from "@/pages/Handover";
import Execution from "@/pages/Execution";
import Approvals from "@/pages/Approvals";

export const PIPELINE = [
  ["assign", "Assign", "Assignments", Assignments],
  ["track", "Track", "Tracker", Tracker],
  ["handover", "Handover", "Handover", Handover],
  ["execution", "Execution", "Execution", Execution],
  ["approvals", "Approvals & QC", "Approvals", Approvals],
];

function StageBar({ stages, value, onChange }) {
  const ref = useRef(null);
  useEffect(() => { ref.current?.querySelector("[data-active=true]")?.scrollIntoView({ block: "nearest", inline: "center" }); }, [value]);
  return (
    <div ref={ref} className="thin-scroll -mx-1 flex items-center gap-1 overflow-x-auto px-1 pb-1" data-testid="pipeline-stage-bar">
      {stages.map(([key, label], i) => (
        <Fragment key={key}>
          <button type="button" data-testid={`pipeline-stage-${key}`} data-active={value === key} onClick={() => onChange(key)}
            className={`flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors ${value === key ? "bg-zinc-900 font-semibold text-white" : "border border-zinc-200 bg-white text-zinc-600 hover:border-violet-300 hover:text-zinc-900"}`}>
            <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold ${value === key ? "bg-violet-400 text-zinc-900" : "bg-zinc-100 text-zinc-500"}`}>{PIPELINE.findIndex(([k]) => k === key) + 1}</span>{label}
          </button>
          {i < stages.length - 1 && <ChevronRight className="h-4 w-4 shrink-0 text-violet-400" />}
        </Fragment>
      ))}
      {stages.length > 1 && <>
      <span className="mx-2 h-6 w-px shrink-0 bg-zinc-200" />
      <button type="button" data-testid="pipeline-stage-all" data-active={value === "all"} onClick={() => onChange("all")}
        className={`flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-sm transition-colors ${value === "all" ? "bg-zinc-900 font-semibold text-white" : "border border-zinc-200 bg-white text-zinc-600 hover:border-violet-300"}`}>
        <LayoutList className="h-4 w-4" />All stages
      </button>
      </>}
    </div>
  );
}

export default function WorkPipeline() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const stages = PIPELINE.filter(([, , mod]) => can(mod));
  const asked = params.get("stage");
  const stage = (asked === "all" && stages.length > 1) || stages.some(([k]) => k === asked) ? asked : stages[0]?.[0];
  const shown = stage === "all" ? stages : stages.filter(([k]) => k === stage);
  return (
    <>
      <PageHeader num="04" title="Work Assignment" subtitle="One pipeline from task assignment through tracking, handover, on-ground execution and final approvals & QC." />
      <StageBar stages={stages} value={stage} onChange={(k) => setParams({ stage: k })} />
      <EmbeddedContext.Provider value={true}>
        {shown.map(([key, label, , Page], i) => (
          <section key={key} data-testid={`pipeline-section-${key}`} className={`space-y-6 ${stage === "all" && i > 0 ? "border-t border-zinc-200 pt-8" : ""}`}>
            {stage === "all" && <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-brand">Stage {PIPELINE.findIndex(([k]) => k === key) + 1} · {label}</div>}
            <Page />
          </section>
        ))}
      </EmbeddedContext.Provider>
    </>
  );
}
